import React, { useEffect, useMemo, useRef } from 'react'
import moment from 'moment'
import { Clock } from '@phosphor-icons/react'
import Spinner from 'components/Spinner'
import { LeagueGamesByRoundDate, LeagueGameRespType } from 'types/league/leagueGame'

interface WeekCalendarProps {
  games: LeagueGamesByRoundDate[] | undefined
  weekStart: moment.Moment
  isLoading?: boolean
}

// 6 AM – 10 PM covers all realistic game times (startTime is UTC; local conversion may shift into evening)
const SLOT_HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22]

const WeekCalendar: React.FC<WeekCalendarProps> = ({ games, weekStart, isLoading }) => {
  const bodyRef = useRef<HTMLDivElement>(null)

  // Scroll to 8 AM row on mount so daytime slots are visible by default
  useEffect(() => {
    if (bodyRef.current) {
      const slotHeight = 80
      const startHour = SLOT_HOURS[0]
      const scrollTo = (8 - startHour) * slotHeight
      bodyRef.current.scrollTop = scrollTo > 0 ? scrollTo : 0
    }
  }, [])

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'days')),
    [weekStart],
  )

  const gameMap = useMemo(() => {
    const map: Record<string, Record<number, LeagueGameRespType[]>> = {}
    games?.forEach(round => {
      round.dates.forEach(d => {
        // Use the group date directly — avoids timezone-shift when game.date is a full ISO string
        const dateStr = d.date.substring(0, 10)
        d.games.forEach(game => {
          const localHour = moment
            .utc()
            .startOf('day')
            .add(game.timings.startTime, 'minutes')
            .local()
            .hour()
          if (!map[dateStr]) map[dateStr] = {}
          if (!map[dateStr][localHour]) map[dateStr][localHour] = []
          map[dateStr][localHour].push(game)
        })
      })
    })
    return map
  }, [games])

  return (
    <div className="week-calendar-container">
      <div className="wc-header">
        <div className="wc-corner">
          <Clock size={18} color="#888" />
        </div>
        {weekDays.map((day, i) => (
          <div key={i} className="wc-day-header">
            {day.format('D MMM, YYYY')}
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className="py-40px flex justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="wc-body" ref={bodyRef}>
          <div className="wc-time-col">
            {SLOT_HOURS.map(h => (
              <div key={h} className="wc-time-label">
                {moment().hour(h).minute(0).format('h:mm a')}
              </div>
            ))}
            <div className="wc-time-label wc-time-label-last">
              {moment().hour(23).minute(0).format('h:mm a')}
            </div>
          </div>

          <div className="wc-grid">
            {SLOT_HOURS.map(hour => (
              <div key={hour} className="wc-row">
                {weekDays.map((day, di) => {
                  const dateStr = day.format('YYYY-MM-DD')
                  const slotGames = gameMap[dateStr]?.[hour] ?? []
                  return (
                    <div key={di} className="wc-cell">
                      {slotGames.map(game => (
                        <div key={game._id} className="wc-game-card">
                          <p className="wc-game-teams">
                            {game.homeTeam?.team?.name ?? 'TBD'} &amp; {game.awayTeam?.team?.name ?? 'TBD'}
                          </p>
                          <p className="wc-game-field">Field {game.courtFieldNumber}</p>
                        </div>
                      ))}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default WeekCalendar
